import * as LDClient from "launchdarkly-js-client-sdk";
import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { createContext } from "react";
import { Platform } from "react-native";

import { Loader } from "@whoosh/design-system/src/components/display";

import { getExpoEASConstant } from "../expoConstants";
import { getLaunchDarklyToken } from "./getLaunchDarklyToken/getLaunchDarklyToken";
import { QueueItLaunchDarklyContext } from "./types";
import { useLaunchDarklyIdentificationQueue } from "./useLaunchDarklyIdentificationQueue";

type LDUser = {
  key: string;
  name: string;
  email?: string;
  custom?: any;
};
type iLaunchDarklyProps = {
  children: ReactNode;
  parentLDContext?: ContextState;
};

const defaultUser = {
  key: "unauthenticated-user",
  name: "Dave Unauthenticated",
};

type ContextState = {
  flags: null | LDClient.LDFlagSet;
  identifyNewUser: (user: LDUser) => Promise<void>;
  identifyQueueItContext: (
    queueItContext: QueueItLaunchDarklyContext
  ) => Promise<void>;
  refreshFlags: () => void;
  hasInitialized: boolean;
};
const LaunchDarklyContext = createContext({} as ContextState);

const LaunchDarklyProvider = ({
  children,
  parentLDContext,
}: iLaunchDarklyProps) => {
  const [hasClient, setHasClient] = useState(false);
  const [client, setClient] = useState<LDClient.LDClient | null>(null);
  const [flags, setFlags] = useState<null | LDClient.LDFlagSet>(null);
  const [hasInitialized, setHasInitialized] = useState(false);

  const launchDarklyToken = getLaunchDarklyToken();

  const [currentUser, setCurrentUser] = useState<LDUser>(defaultUser);
  const currentUserRef = useRef(currentUser);
  const enqueueIdentification = useLaunchDarklyIdentificationQueue();

  useEffect(() => {
    currentUserRef.current = currentUser;
  }, [currentUser]);

  /**
   * Check for an outer context so that we aren't re-instantiating an LD client
   * This will, if one is present, just relay it down. If one is not present - set up a new one.
   */
  const contextCheck = useContext(LaunchDarklyContext);
  const outerContext = parentLDContext ?? contextCheck;

  /**
   * Allow children to identify a new user in case of auth change
   */
  const identifyNewUser = useCallback(
    (user: LDUser) => {
      const identifyUser = async () => {
        const identifiedUser = {
          ...user,
          custom: {
            ...currentUserRef.current.custom,
            ...user.custom,
          },
        };

        if (Platform.OS === "web" && client) {
          if (user.key === "unknown-user") {
            const { key, ...restUser } = user;
            await client.identify({
              anonymous: true,
              ...restUser,
            });
          }
          await client.identify(identifiedUser);
          setFlags(client.allFlags());
        }

        currentUserRef.current = identifiedUser;
        setCurrentUser(identifiedUser);
      };

      return enqueueIdentification(identifyUser);
    },
    [client, enqueueIdentification]
  );

  const identifyQueueItContext = useCallback(
    ({ clubSlug, platform }: QueueItLaunchDarklyContext) => {
      const identifyQueueItContext = async () => {
        const identifiedUser = {
          ...currentUserRef.current,
          custom: {
            ...currentUserRef.current.custom,
            clubSlug,
            platform,
          },
        };

        if (client) {
          await client.identify(identifiedUser);
          setFlags(client.allFlags());
        }

        currentUserRef.current = identifiedUser;
        setCurrentUser(identifiedUser);
      };

      return enqueueIdentification(identifyQueueItContext);
    },
    [client, enqueueIdentification]
  );

  /**
   * callback to allow children to refresh flags
   */
  const refreshFlags = useCallback(() => {
    if (client) {
      setFlags(client.allFlags());
    }
  }, [client]);

  /**
   * Initialize first client
   */
  useEffect(() => {
    const shouldInitializeNewClient =
      !hasInitialized && !outerContext.hasInitialized && client === null;

    if (shouldInitializeNewClient) {
      setHasInitialized(true);
      if (Platform.OS === "web") {
        const shouldDisableWebsockets: string = getExpoEASConstant(
          "disableLaunchDarklyWebsockets"
        );
        const shouldStream = !shouldDisableWebsockets;
        const client =
          currentUser &&
          LDClient.initialize(launchDarklyToken, currentUser, {
            streaming: shouldStream,
          });

        client?.waitUntilReady().then(() => {
          setClient(client);
          setFlags(client.allFlags());
          setHasClient(true);
        });
      }
    }
  }, [
    launchDarklyToken,
    currentUser,
    client,
    outerContext.flags,
    hasInitialized,
    outerContext.hasInitialized,
  ]);

  /**
   * Handle Web client flag updates
   */
  useEffect(() => {
    if (client) {
      client.on("change", () => setFlags(client.allFlags()));
    }
  }, [client, setFlags]);

  /**
   * If we have an outer context, we just pipe those values along rather than re-instantiating
   * a client. This way nesting these contexts still uses one top level state.
   * This is necessary due to how portals will nuke surrounding contexts and occasionally we need
   * to pass our context down into that portal.
   */
  const contextValues = outerContext.flags
    ? outerContext
    : {
        flags,
        identifyNewUser,
        identifyQueueItContext,
        refreshFlags,
        hasInitialized,
      };

  return (
    <LaunchDarklyContext.Provider value={contextValues}>
      {Platform.OS !== "web" || hasClient || outerContext.flags ? (
        children
      ) : (
        <Loader />
      )}
    </LaunchDarklyContext.Provider>
  );
};

export default LaunchDarklyContext;
export { LaunchDarklyProvider };
